<template>
  <v-card outlined class="mt-3">
    <v-card-subtitle class="d-flex align-center py-2">
      Plan Prices

      <v-menu offset-y max-height="420">
        <template v-slot:activator="{on, attrs}">
          <v-chip class="ml-2" small label outlined style="cursor: pointer" v-bind="attrs" v-on="on">
            <template v-if="planId">
              <span v-if="plan && plan.name">{{ plan.name }}</span>
              <span class="ml-1 grey--text">({{ planId }})</span>
            </template>
            <span v-else>select a plan</span>
            <v-icon x-small right>mdi-menu-down</v-icon>
          </v-chip>
        </template>
        <v-list dense>
          <v-list-item
            v-for="p of planOptions"
            :key="p.id"
            :input-value="p.id === planId"
            @click="selectPlan(p.id)"
          >
            <v-list-item-content>
              <v-list-item-title>
                <span v-if="p.name">{{ p.name }} </span><span class="grey--text">({{ p.id }})</span>
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-icon v-if="p.id === activePlanId">
              <v-icon small color="primary">mdi-account-check</v-icon>
            </v-list-item-icon>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-chip
        v-if="overridePlanId && overridePlanId !== activePlanId"
        class="ml-2"
        x-small
        color="warning"
        @click="overridePlanId = null"
      >
        previewing — reset to active
        <v-icon x-small right>mdi-close</v-icon>
      </v-chip>

      <v-spacer/>
      <span v-if="plan && plan.stream" class="text-caption grey--text">stream: {{ plan.stream }}</span>
    </v-card-subtitle>

    <v-card-text v-if="!planId" class="py-2">
      <v-alert dense text type="info" class="mb-0">No active plan for this customer.</v-alert>
    </v-card-text>

    <v-card-text v-else-if="!plan" class="py-2">
      <v-alert dense text type="warning" class="mb-0">Plan "{{ planId }}" not found.</v-alert>
    </v-card-text>

    <v-card-text v-else class="py-2">
      <div class="d-flex flex-wrap align-start" style="gap: 24px">

        <!-- Plan prices -->
        <div style="flex: 1 1 320px; min-width: 0">
          <div class="text-caption font-weight-medium mb-1">Plan price by weekly meal count</div>
          <v-simple-table v-if="sortedRanges.length" dense>
            <template v-slot:default>
              <thead>
              <tr>
                <th>Meals</th>
                <th>Description</th>
                <th class="text-right">Price / meal</th>
                <th class="text-right">Large</th>
                <th class="text-center">Free Shipping</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(r, i) of sortedRanges" :key="i">
                <td>{{ rangeLabel(r) }}</td>
                <td class="text-caption">{{ r.description || '-' }}</td>
                <td class="text-right">{{ formatCurrency(Number(r.price)) }}</td>
                <td class="text-right">{{ largePrice ? formatCurrency(Number(r.price) + largePrice) : '-' }}</td>
                <td class="text-center">
                  <v-icon v-if="r.free_shipping" small color="success">mdi-check</v-icon>
                </td>
              </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div v-else-if="plan.per_meal" class="text-body-2">
            Flat price: <strong>{{ formatCurrency(Number(plan.per_meal)) }}</strong> / meal
          </div>
          <div v-else class="text-caption grey--text">
            No pricing ranges defined for this plan.
          </div>

          <div class="mt-2 text-caption grey--text d-flex flex-wrap" style="gap: 16px">
            <span v-if="largePrice">Large surcharge: {{ formatCurrency(largePrice) }} (not applied to breakfast)</span>
            <span v-if="adjustSummary">Adjustments: {{ adjustSummary }}</span>
            <span v-if="swapAtPlanRate">Swap meal price: charged at plan rate (varies by meal count, see above)</span>
          </div>
        </div>

        <!-- Swap prices -->
        <div v-if="!swapAtPlanRate && swapStreamPrices.length" style="flex: 1 1 320px; min-width: 0">
          <div class="text-caption font-weight-medium mb-1">
            Swap meal price by stream (single-meal rate, no volume discount)
          </div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
              <tr>
                <th>Stream</th>
                <th class="text-right">Price / meal</th>
                <th class="text-right">Large</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="s of swapStreamPrices" :key="s.id">
                <td>
                  {{ s.name }}
                  <span v-if="s.isOwnStream" class="grey--text text-caption">(this plan's stream)</span>
                </td>
                <td class="text-right">{{ formatCurrency(s.price) }}</td>
                <td class="text-right">{{ s.largePrice !== s.price ? formatCurrency(s.largePrice) : '-' }}</td>
              </tr>
              </tbody>
            </template>
          </v-simple-table>
          <div class="text-caption grey--text mt-1">Large price is not applied to breakfast.</div>
        </div>

      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import {mapActions, mapState} from "vuex";
import {formatCurrency} from "@/store/utils";
import {findRange} from "fed-lib/src/billing/prices-explain";

export default {
  name: "PlanPrices",
  props: {
    // the full customer/user object; the active plan is resolved from it
    customer: {type: Object, default: null},
  },
  data() {
    return {
      // when set, preview a different plan instead of the customer's active one
      overridePlanId: null,
    };
  },
  computed: {
    ...mapState(["plans", "streams"]),
    // mirrors fed-lib getActivePlanId: pending edits win over the saved plan
    activePlanId() {
      const c = this.customer;
      if (!c) return null;
      return (c.pendingEdits && c.pendingEdits.plan) || c.plan || null;
    },
    // the plan currently being displayed (active plan, or a previewed override)
    planId() {
      return this.overridePlanId || this.activePlanId;
    },
    plan() {
      return this.planId && this.plans ? this.plans[this.planId] : null;
    },
    // all plans for the switcher. Ordering:
    //   0. visible stream plans (plan id matches a visible stream)
    //   1. plans ending in "classic6"
    //   2. other plans ending in "classic*"
    //   3. everything else
    planOptions() {
      const plans = this.plans || {};
      const streams = this.streams || {};
      const isVisibleStream = id => !!(streams[id] && streams[id].visible);
      const isClassic6 = id => /classic6$/i.test(id);
      const isClassic = id => /classic\d*$/i.test(id);
      const rankOf = id => {
        if (isVisibleStream(id)) return 0;
        if (isClassic6(id)) return 1;
        if (isClassic(id)) return 2;
        return 3;
      };
      return Object.keys(plans)
        .map(id => ({id, name: plans[id] && plans[id].name, rank: rankOf(id)}))
        .sort((a, b) => {
          if (a.rank !== b.rank) return a.rank - b.rank;
          // classic groups sort by id (numeric-aware), others by display name
          const useId = a.rank === 1 || a.rank === 2;
          const ka = useId ? a.id : (a.name || a.id);
          const kb = useId ? b.id : (b.name || b.id);
          return ka.localeCompare(kb, undefined, {numeric: true});
        });
    },
    largePrice() {
      return this.plan ? Number(this.plan.large_price || 0) : 0;
    },
    sortedRanges() {
      if (!this.plan || !this.plan.ranges) return [];
      return [...this.plan.ranges]
        .filter(Boolean)
        .sort((a, b) => (Number(a.min) || 0) - (Number(b.min) || 0));
    },
    adjustSummary() {
      const a = this.plan && this.plan.adjust;
      if (!a) return "";
      return Object.keys(a)
        .filter(k => Number(a[k]))
        .map(k => `${k} ${this.formatCurrency(Number(a[k]))}`)
        .join(", ");
    },
    // Whether swaps are charged at the normal plan rate (varies by meal count)
    // rather than a per-stream single-meal rate.
    swapAtPlanRate() {
      return !!(this.plan && this.plan.swap_at_plan_rate);
    },
    // When NOT swapping at plan rate, a swapped meal is priced at the single-meal
    // rate (1 meal, no volume discount) of whichever stream it is swapped to.
    // List that price for each available stream.
    swapStreamPrices() {
      if (!this.plan || this.swapAtPlanRate) return [];
      const streams = this.streams || {};
      return Object.keys(streams)
        .filter(id => id !== "diabetes" && streams[id] && streams[id].visible)
        .map(id => {
          const p = this.singleMealPrice(this.plans && this.plans[id]);
          if (!p) return null;
          return {
            id,
            name: (streams[id] && streams[id].name) || id,
            isOwnStream: id === this.plan.stream,
            ...p,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  watch: {
    // a new customer resets any previewed plan back to their active plan
    customer() {
      this.overridePlanId = null;
    },
  },
  methods: {
    ...mapActions(["fetchPlans", "fetchStreams"]),
    formatCurrency,
    selectPlan(id) {
      this.overridePlanId = id;
    },
    // single-meal rate for a plan: the range that covers 1 meal (no volume
    // discount), falling back to the flat per_meal price.
    singleMealPrice(plan) {
      if (!plan) return null;
      const range = findRange(plan, 1);
      let base = null;
      if (range) base = Number(range.price);
      else if (plan.per_meal && Number(plan.per_meal)) base = Number(plan.per_meal);
      if (base == null) return null;
      return {price: base, largePrice: base + Number(plan.large_price || 0)};
    },
    rangeLabel(r) {
      const has = v => v !== undefined && v !== null && v !== "";
      const min = has(r.min) ? Number(r.min) : null;
      const max = has(r.max) ? Number(r.max) : null;
      if (min !== null && max !== null) return `${min}–${max}`;
      if (min !== null) return `${min}+`;
      if (max !== null) return `up to ${max}`;
      return "any";
    },
  },
  mounted() {
    if (!this.plans || !Object.keys(this.plans).length) {
      this.fetchPlans();
    }
    if (!this.streams || !Object.keys(this.streams).length) {
      this.fetchStreams();
    }
  },
};
</script>
